import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';

const PROVIDER_LABELS = {
  openai: 'OpenAI',
  azure: 'Azure OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Google Gemini',
  grok: 'Grok (xAI)',
};

const PLACEHOLDER_MAP = {
  openai: 'sk-...',
  azure: 'Azure API key',
  anthropic: 'sk-ant-...',
  gemini: 'AIza...',
  grok: 'xai-...',
};

const truncateError = (msg, maxLen = 200) => {
  if (!msg || msg.length <= maxLen) return msg;
  return msg.substring(0, maxLen).replace(/[{[\s]+$/, '') + '…';
};

const AIProviderModal = ({ isOpen, onClose, organizationId, providerName, models = [], onSuccess }) => {
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  // Azure-specific fields
  const [endpoint, setEndpoint] = useState('');
  const [deploymentName, setDeploymentName] = useState('');
  const [apiVersion, setApiVersion] = useState('2024-02-15-preview');

  const isAzure = providerName === 'azure';
  const label = PROVIDER_LABELS[providerName] || providerName;

  useEffect(() => {
    if (!isOpen) {
      setApiKey('');
      setShowApiKey(false);
      setTestResult(null);
      setError(null);
      setEndpoint('');
      setDeploymentName('');
      setApiVersion('2024-02-15-preview');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleTest = async () => {
    if (!apiKey) { setError('Please enter an API key'); return; }
    if (isAzure && (!endpoint || !deploymentName)) { setError('Please fill in all Azure fields'); return; }

    setTesting(true);
    setError(null);
    setTestResult(null);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const body = { apiKey };
      if (isAzure) { body.endpoint = endpoint; body.deploymentName = deploymentName; body.apiVersion = apiVersion; }

      const response = await axios.post(
        `${apiUrl}/ai-providers/test/${providerName}`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;
      if (data.success) {
        setTestResult(data);
        setError(null);
      } else {
        const cleanMsg = truncateError(data.message);
        setTestResult({ ...data, message: cleanMsg });
        setError(null);
      }
    } catch (err) {
      const msg = truncateError(err.response?.data?.message || 'Failed to test connection');
      setTestResult({ success: false, message: msg });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey) { setError('Please enter an API key'); return; }
    if (isAzure && (!endpoint || !deploymentName)) { setError('Please fill in all Azure fields'); return; }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const body = { apiKey, enabled: true };
      if (isAzure) { body.endpoint = endpoint; body.deploymentName = deploymentName; body.apiVersion = apiVersion; }

      await axios.put(
        `${apiUrl}/ai-providers/${organizationId}/provider/${providerName}`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-black-0">Configure {label}</h2>
            <button onClick={onClose} className="text-grey-60 hover:text-black-0 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-black-0 mb-1">{label} API Key *</label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => { setApiKey(e.target.value); setError(null); setTestResult(null); }}
                  placeholder={PLACEHOLDER_MAP[providerName] || 'API key'}
                  className="w-full px-3 py-2 border border-grey-20 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
                <button type="button" onClick={() => setShowApiKey(!showApiKey)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-grey-60 hover:text-black-0">
                  {showApiKey ? (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-grey-60">Your API key will be encrypted and stored securely</p>
            </div>

            {/* Azure-specific fields */}
            {isAzure && (
              <>
                <div>
                  <label className="block text-sm font-medium text-black-0 mb-1">Endpoint *</label>
                  <input
                    type="text"
                    value={endpoint}
                    onChange={(e) => setEndpoint(e.target.value)}
                    placeholder="https://your-resource.openai.azure.com"
                    className="w-full px-3 py-2 border border-grey-20 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black-0 mb-1">Deployment Name *</label>
                  <input
                    type="text"
                    value={deploymentName}
                    onChange={(e) => setDeploymentName(e.target.value)}
                    placeholder="gpt-4o"
                    className="w-full px-3 py-2 border border-grey-20 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black-0 mb-1">API Version</label>
                  <input
                    type="text"
                    value={apiVersion}
                    onChange={(e) => setApiVersion(e.target.value)}
                    placeholder="2024-02-15-preview"
                    className="w-full px-3 py-2 border border-grey-20 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                  />
                </div>
              </>
            )}

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Test result */}
            {testResult && (
              <div className={`p-3 rounded-md ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <p className={`text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>{testResult.message}</p>
              </div>
            )}

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-xs text-blue-800">
                Your API key will be used for all AI analysis in this organization.
                Make sure you have sufficient credits in your {label} account.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-3 justify-end">
            <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm font-medium text-black-0 bg-white border border-grey-30 rounded-md hover:bg-grey-10 transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleTest} disabled={testing || !apiKey || loading} className="px-4 py-2 text-sm font-medium text-black-0 bg-white border border-grey-30 rounded-md hover:bg-grey-10 transition-colors disabled:opacity-50">
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
            <button onClick={handleSave} disabled={loading || !apiKey || (testResult && !testResult.success)} className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIProviderModal;
